import { useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, Scale } from 'lucide-react';
import { useStore } from '../store/useStore';
import { formatCurrency, formatDateTime, isAdminRole } from '../lib/utils';
import { computeProfitability, costBarColor, formatTracked } from '../lib/profitability';
import { syncAllAndWait, useClickUpStatus } from '../lib/clickupStatus';
import type { Project, ProjectStatus } from '../types';

type SortCol = 'name' | 'hours' | 'cost' | 'value' | 'margin' | 'marginPct' | 'rate' | 'ratio';

const SORT_LABELS: Record<SortCol, string> = {
  name: 'projekt',
  hours: 'tunnid',
  cost: 'aja kulu',
  value: 'koguväärtus',
  margin: 'marginaal €',
  marginPct: 'marginaal %',
  rate: '€/h',
  ratio: 'kulu vs koguväärtus',
};

const STATUS_FILTERS: { value: 'all' | ProjectStatus; label: string }[] = [
  { value: 'all', label: 'Kõik staatused' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

/** Agency-wide profitability: every ClickUp-tracked project, sortable by any
 *  column (default: worst margin first). */
export default function ProfitabilityTable({
  projects,
  clientName,
  onOpen,
}: {
  projects: Project[];
  clientName: (clientId: string) => string;
  onOpen: (projectId: string) => void;
}) {
  const { status, refresh } = useClickUpStatus();
  const currentUser = useStore((s) => s.currentUser);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const isAdmin = isAdminRole(currentUser?.role);

  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'margin', dir: 'asc' });
  const [statusFilter, setStatusFilter] = useState<'all' | ProjectStatus>('all');

  const allRows = useMemo(() => {
    if (!status) return [];
    return projects
      .filter((p) => p.clickupListId && p.clickupSyncedAt)
      .map((p) => {
        const value = Number(p.value);
        const trackedMs = p.clickupTrackedMs ?? 0;
        return { project: p, value, trackedMs, ...computeProfitability(value, trackedMs, status.hourlyCost) };
      });
  }, [projects, status]);

  const rows = useMemo(
    () => (statusFilter === 'all' ? allRows : allRows.filter((r) => r.project.status === statusFilter)),
    [allRows, statusFilter],
  );
  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    allRows.forEach((r) => counts.set(r.project.status, (counts.get(r.project.status) ?? 0) + 1));
    return counts;
  }, [allRows]);

  // Empty values (no hours / no project value) always sink to the bottom.
  const sortedRows = useMemo(() => {
    const valueOf = (r: (typeof rows)[number]): number | string | null => {
      switch (sort.col) {
        case 'name': return r.project.name;
        case 'hours': return r.trackedMs;
        case 'cost': return r.cost;
        case 'value': return r.value;
        case 'margin': return r.margin;
        case 'marginPct': return r.marginPercent;
        case 'rate': return r.effectiveRate;
        case 'ratio': return r.costRatio;
      }
    };
    return [...rows].sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      if (va == null || vb == null) return va == null ? (vb == null ? 0 : 1) : -1;
      const cmp = typeof va === 'string' ? va.localeCompare(String(vb), 'et') : va - (vb as number);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort]);

  const toggleSort = (col: SortCol) =>
    setSort((prev) => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const sortButton = (col: SortCol, label: string) => (
    <button
      onClick={() => toggleSort(col)}
      className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-600 ${sort.col === col ? 'text-gray-600' : ''}`}
    >
      {label}
      <span className={sort.col === col ? 'text-brand-500' : 'text-gray-300'}>
        {sort.col === col ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );

  const totals = useMemo(
    () => rows.reduce(
      (t, r) => ({ ms: t.ms + r.trackedMs, cost: t.cost + r.cost, value: t.value + r.value, margin: t.margin + r.margin }),
      { ms: 0, cost: 0, value: 0, margin: 0 },
    ),
    [rows],
  );
  const totalHours = totals.ms / 3_600_000;
  const totalRatio = totals.value > 0 ? totals.cost / totals.value : null;
  const underwater = rows.filter((r) => r.margin < 0).length;
  const pending = projects.filter((p) => p.clickupListId && !p.clickupSyncedAt).length;
  const unlinked = projects.filter((p) => !p.clickupListId && Number(p.value) > 0).length;
  const running = syncing || !!status?.syncing;

  const syncAll = async () => {
    setSyncing(true);
    setError('');
    try {
      await syncAllAndWait();
      await refresh();
    } catch (e) {
      setError((e as Error)?.message ?? 'Sünkroonimine ebaõnnestus.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100">
      <div className="px-5 py-4 border-b border-gray-50 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-gray-900 text-sm">Projektide tasuvus</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            ClickUp'i tunnid × {status ? formatCurrency(status.hourlyCost) : '—'}/h ·{' '}
            {sort.col === 'margin' && sort.dir === 'asc'
              ? 'halvim marginaal eespool'
              : `sorteeritud: ${SORT_LABELS[sort.col]} ${sort.dir === 'asc' ? '↑' : '↓'}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {allRows.length > 0 && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | ProjectStatus)}
              className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {STATUS_FILTERS.filter((o) => o.value === 'all' || statusCounts.get(o.value)).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} ({o.value === 'all' ? allRows.length : statusCounts.get(o.value)})
                </option>
              ))}
            </select>
          )}
          {status?.lastSync && (
            <span className="text-xs text-gray-400">Sünkroonitud {formatDateTime(status.lastSync)}</span>
          )}
          {isAdmin && status?.configured && (
            <button
              onClick={syncAll}
              disabled={running}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
            >
              <RefreshCw size={12} className={running ? 'animate-spin' : ''} />
              {running ? 'Sünkroonin…' : 'Sünkrooni kõik'}
            </button>
          )}
        </div>
      </div>

      {error && <p className="px-5 pt-3 text-xs text-red-500">{error}</p>}

      {status && !status.configured && (
        <p className="px-5 py-8 text-center text-sm text-gray-400">
          ClickUp pole ühendatud — serveris on vaja <code className="text-xs bg-gray-100 px-1 rounded">CLICKUP_API_TOKEN</code>.
        </p>
      )}

      {status?.configured && allRows.length === 0 && (
        <div className="px-5 py-10 text-center">
          <Scale size={36} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm text-gray-400">
            Ühtegi ClickUpiga seotud ja sünkroonitud projekti pole. Seo projekt ClickUp'i listiga projekti ülevaate lehel.
          </p>
        </div>
      )}

      {status?.configured && allRows.length > 0 && rows.length === 0 && (
        <p className="px-5 py-10 text-center text-sm text-gray-400">Selle staatusega projekte pole.</p>
      )}

      {status?.configured && rows.length > 0 && (
        <>
          {/* Totals at a glance */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 px-5 py-4 border-b border-gray-50">
            <div>
              <p className="text-xs text-gray-400">Tunnid kokku</p>
              <p className="text-lg font-bold text-gray-900">{formatTracked(totals.ms)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Aja kulu kokku</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(totals.cost)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Marginaal kokku</p>
              <p className={`text-lg font-bold ${totals.margin < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(totals.margin)}
                {totals.value > 0 && <span className="text-xs font-medium ml-1">({Math.round((totals.margin / totals.value) * 100)}%)</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Kahjumis projekte</p>
              <p className={`text-lg font-bold ${underwater > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {underwater} / {rows.length}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {([
                    ['name', 'Projekt'],
                    ['hours', 'Tunnid'],
                    ['cost', 'Aja kulu'],
                    ['value', 'Koguväärtus'],
                  ] as [SortCol, string][]).map(([col, label]) => (
                    <th key={col} className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 whitespace-nowrap">{sortButton(col, label)}</th>
                  ))}
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2.5">
                      {sortButton('margin', 'Marginaal')}
                      {sortButton('marginPct', '%')}
                    </span>
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 whitespace-nowrap">{sortButton('rate', '€/h')}</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 whitespace-nowrap">{sortButton('ratio', 'Kulu vs koguväärtus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sortedRows.map((r) => (
                  <tr key={r.project.id} onClick={() => onOpen(r.project.id)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 max-w-[240px]">
                      <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                        {r.project.name}
                        {r.project.clickupSyncError && (
                          <span title={`Viimane sünk ebaõnnestus: ${r.project.clickupSyncError}`}>
                            <AlertTriangle size={12} className="text-amber-500 shrink-0" />
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{clientName(r.project.clientId)}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">{formatTracked(r.trackedMs)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">{formatCurrency(r.cost)}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">{formatCurrency(r.value)}</td>
                    <td className={`px-4 py-3 text-xs font-semibold whitespace-nowrap ${r.margin < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(r.margin)}
                      {r.marginPercent != null && <span className="font-normal ml-1">({Math.round(r.marginPercent * 100)}%)</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {r.effectiveRate != null ? formatCurrency(r.effectiveRate) : '—'}
                    </td>
                    <td className="px-4 py-3 w-40">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${costBarColor(r.costRatio)}`} style={{ width: `${Math.min(r.costRatio ?? 0, 1) * 100}%` }} />
                        </div>
                        <span className={`text-[11px] w-9 text-right ${r.costRatio != null && r.costRatio > 1 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                          {r.costRatio != null ? `${Math.round(r.costRatio * 100)}%` : '—'}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td className="px-4 py-3 text-xs font-semibold text-gray-600">Kokku ({rows.length})</td>
                  <td className="px-4 py-3 text-xs font-bold text-gray-900 whitespace-nowrap">{formatTracked(totals.ms)}</td>
                  <td className="px-4 py-3 text-xs font-bold text-gray-900 whitespace-nowrap">{formatCurrency(totals.cost)}</td>
                  <td className="px-4 py-3 text-xs font-bold text-gray-900 whitespace-nowrap">{formatCurrency(totals.value)}</td>
                  <td className={`px-4 py-3 text-xs font-bold whitespace-nowrap ${totals.margin < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(totals.margin)}
                  </td>
                  <td className="px-4 py-3 text-xs font-bold text-gray-900 whitespace-nowrap">
                    {totalHours > 0 ? formatCurrency(totals.value / totalHours) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${costBarColor(totalRatio)}`} style={{ width: `${Math.min(totalRatio ?? 0, 1) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {status?.configured && (pending > 0 || unlinked > 0) && (
        <p className="px-5 py-3 border-t border-gray-50 text-xs text-gray-400">
          {pending > 0 && `${pending} seotud projekti ootab sünkroonimist. `}
          {unlinked > 0 && `${unlinked} projekti pole ClickUp'iga seotud.`}
        </p>
      )}
    </div>
  );
}
